import React, { useState, useEffect } from 'react';
import { Check, X, AlertCircle, LogOut, RefreshCw, Key, Shield, UserCheck, UserX, Database, Calendar, User, Users } from 'lucide-react';
import api from '../api/api';

const UserApprovalPage = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Stats for dashboard
  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    sectionUsers: 0,
    entryUsers: 0,
    totalUsers: 0
  });

  // Check if logged in user is Admin
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isAdmin = userData.userType === 'Admin';
  
  useEffect(() => {
    // Only fetch users if admin
    if (isAdmin) {
      fetchPendingUsers();
      fetchAllUsers();
    }
  }, [isAdmin]);

  const fetchPendingUsers = async () => {
    try {
      setIsLoading(true);
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Include token in the request header matching the format used in FormsPage
      const response = await api.get('/getPendingUsers', {
        headers: {
          'Authorization': token
        }
      });
      
      if (response.data && response.data.results) {
        // Filter out any admin users that might be in the pending list
        const nonAdminPendingUsers = response.data.results.filter(
          user => user.userType !== 'Admin'
        );
        setPendingUsers(nonAdminPendingUsers);
      }
    } catch (err) {
      console.error('Failed to fetch pending users:', err);
      setError('Failed to load pending user requests.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Fetch all users
      const response = await api.get('/getAllUsers', {
        headers: {
          'Authorization': token
        }
      });
      
      if (response.data && response.data.results) {
        // Store all users
        setAllUsers(response.data.results);
        
        // Get active users (with status 'approved')
        const approvedUsers = response.data.results.filter(
          user => user.status === 'approved'
        );
        setActiveUsers(approvedUsers);

        // Calculate stats
        const pendingCount = response.data.results.filter(user => user.status === 'pending').length;
        const approvedCount = approvedUsers.length;
        const rejectedCount = response.data.results.filter(user => user.status === 'rejected').length;
        const sectionUsers = response.data.results.filter(user => user.userType === 'Section').length;
        const entryUsers = response.data.results.filter(user => user.userType === 'Entry').length;
        const totalUsers = response.data.results.length;

        setStats({
          pendingCount,
          approvedCount,
          rejectedCount,
          sectionUsers,
          entryUsers,
          totalUsers
        });
      }
    } catch (err) {
      console.error('Failed to fetch all users:', err);
    }
  };

  const handleStatusUpdate = async (userId, status) => {
    try {
      setIsLoading(true);
      
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Include token in the request header
      await api.post('/updateUserStatus', 
        {
          userId,
          status
        },
        {
          headers: {
            'Authorization': token
          }
        }
      );
      
      // Remove the user from the pending list
      setPendingUsers(pendingUsers.filter(user => user.id !== userId));
      
      // Refresh the active users list if a user was approved
      if (status === 'approved') {
        fetchAllUsers();
      }
      
      setSuccessMessage(`User ${status} successfully.`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Failed to ${status} user:`, err);
      setError(`Failed to ${status} user. Please try again.`);
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceLogout = async (userId, userName) => {
    try {
      setIsLogoutLoading(true);
      
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Call the force logout API
      await api.post('/forceLogoutUser', 
        {
          userId
        },
        {
          headers: {
            'Authorization': token
          }
        }
      );
      
      // Refresh both user lists
      fetchAllUsers();
      fetchPendingUsers();
      
      setSuccessMessage(`User ${userName} has been logged out.`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to force logout user:', err);
      setError('Failed to force logout user. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLogoutLoading(false);
    }
  };

  const openResetPasswordModal = (user) => {
    setResetUser(user);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetUser || !newPassword) {
      setError('User and new password are required');
      return;
    }

    try {
      setIsResetPasswordLoading(true);
      
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Call the password reset API
      await api.post('/resetPassword', 
        {
          userId: resetUser.id,
          newPassword
        },
        {
          headers: {
            'Authorization': token
          }
        }
      );
      
      // Close the modal and clear form
      setShowResetModal(false);
      setNewPassword('');
      setResetUser(null);
      
      setSuccessMessage(`Password for ${resetUser.userName} has been reset successfully.`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to reset password:', err);
      setError('Failed to reset password. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsResetPasswordLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      setIsLoading(true);
      
      // Get authentication token from localStorage
      const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
      const token = loginResponse?.output?.token;
      
      // Call the backup API endpoint with the token (using GET method)
      await api.get('/backup_data', {
        headers: {
          'Authorization': token
        }
      });
      
      setSuccessMessage('System backup completed successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to backup data:', err);
      setError('Failed to backup data. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a random strong password
  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
  };

  // Format date string to a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format creation date string to a more readable format
  const formatCreationDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  // Get user type badge color
  const getUserTypeBadgeClass = (userType) => {
    switch (userType) {
      case 'Admin':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Section':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Entry':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'Report':
        return 'bg-teal-50 text-teal-700 border-teal-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header Section */}
        <div className="mb-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                <Shield className="mr-3 h-8 w-8 text-indigo-600" />
                User Management Portal
              </h1>
              <p className="text-gray-500">Enterprise solution for user administration and access control</p>
            </div>
          {/* /  <button
              onClick={handleBackup}
              disabled={isLoading}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-all duration-200 flex items-center font-medium"
            >
              <Database className="h-5 w-5 mr-2" />
              {isLoading ? 'Processing...' : 'Backup System'}
            </button> */}
          </div>
        </div>

        {/* Dashboard Stats Cards */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Users Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="p-4 rounded-lg bg-indigo-50 text-indigo-600">
                <Users className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-gray-500 text-sm font-medium">Total Users</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.totalUsers}</h3>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-500">Section Users</p>
                <p className="text-sm font-medium text-gray-800">{stats.sectionUsers}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Entry Users</p>
                <p className="text-sm font-medium text-gray-800">{stats.entryUsers}</p>
              </div>
            </div>
          </div>

          {/* Pending Users Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="p-4 rounded-lg bg-amber-50 text-amber-600">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-gray-500 text-sm font-medium">Pending Approvals</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.pendingCount}</h3>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center text-amber-600">
                <User className="h-4 w-4 mr-1" />
                <p className="text-sm">Requires admin action</p>
              </div>
            </div>
          </div>

          {/* Approved Users Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="p-4 rounded-lg bg-emerald-50 text-emerald-600">
                <UserCheck className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-gray-500 text-sm font-medium">Active Users</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.approvedCount}</h3>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center text-emerald-600">
                <Check className="h-4 w-4 mr-1" />
                <p className="text-sm">Approved accounts</p>
              </div>
            </div>
          </div>

          {/* Rejected Users Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="p-4 rounded-lg bg-red-50 text-red-600">
                <UserX className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-gray-500 text-sm font-medium">Rejected Users</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.rejectedCount}</h3>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center text-red-600">
                <X className="h-4 w-4 mr-1" />
                <p className="text-sm">Access denied</p>
              </div>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 p-4 rounded-lg shadow-sm animate-fadeIn">
            <div className="flex">
              <Check className="h-5 w-5 mr-2 mt-0.5" />
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow-sm animate-fadeIn">
            <div className="flex">
              <AlertCircle className="h-5 w-5 mr-2 mt-0.5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Recent Pending Users */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
            Pending Approval Requests
          </h2>
          <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent"></div>
                <p className="text-gray-500 mt-4">Loading pending requests...</p>
              </div>
            ) : pendingUsers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No pending approval requests.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created On
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-amber-50/30 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.userName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getUserTypeBadgeClass(user.userType)}`}>
                            {user.userType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 flex items-center">
                            <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                            {formatCreationDate(user.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleStatusUpdate(user.id, 'approved')}
                              className="text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
                              disabled={isLoading}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              <span>Approve</span>
                            </button>
                            
                            <button
                              onClick={() => handleStatusUpdate(user.id, 'rejected')}
                              className="text-red-600 hover:text-red-900 hover:bg-red-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
                              disabled={isLoading}
                            >
                              <X className="h-4 w-4 mr-1" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* All Users Section */}
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <UserCheck className="h-5 w-5 mr-2 text-indigo-600" />
            User Directory
          </h2>
          <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent"></div>
                <p className="text-gray-500 mt-4">Loading user data...</p>
              </div>
            ) : allUsers.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No users found in the system.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Type
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allUsers.map((user) => (
                      <tr key={user.id} className={user.status === 'pending' ? 'bg-amber-50/30' : 'hover:bg-gray-50 transition-colors duration-150'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.userName}</div>
                          <div className="text-xs text-gray-500">{formatCreationDate(user.createdAt)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getUserTypeBadgeClass(user.userType)}`}>
                            {user.userType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadgeClass(user.status)}`}>
                            {user.status === 'approved' ? 'Approved' : 
                             user.status === 'pending' ? 'Pending' : 'Rejected'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(user.lastLoginDate)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => openResetPasswordModal(user)}
                              className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
                              disabled={isLoading}
                            >
                              <Key className="h-4 w-4 mr-1" />
                              <span>Reset</span>
                            </button>
                            
                            {user.status === 'approved' && (
                              <button
                                onClick={() => handleForceLogout(user.id, user.userName)}
                                className="text-red-600 hover:text-red-900 hover:bg-red-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
                                disabled={isLogoutLoading}
                              >
                                <LogOut className="h-4 w-4 mr-1" />
                                <span>Logout</span>
                              </button>
                            )}
                            
                            {user.status === 'pending' && (
  <>
    <button
      onClick={() => handleStatusUpdate(user.id, 'approved')}
      className="text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
      disabled={isLoading}
    >
      <Check className="h-4 w-4 mr-1" />
      <span>Approve</span>
    </button>
    
    <button
      onClick={() => handleStatusUpdate(user.id, 'rejected')}
      className="text-red-600 hover:text-red-900 hover:bg-red-50 py-1 px-3 rounded-md transition-colors duration-150 inline-flex items-center"
      disabled={isLoading}
    >
      <X className="h-4 w-4 mr-1" />
      <span>Reject</span>
    </button>
  </>
)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Reset Password Modal */}
        {showResetModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm"></div>
            <div className="bg-white p-8 rounded-xl shadow-xl z-10 w-full max-w-md border border-gray-200 animate-scaleIn">
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                <Key className="h-5 w-5 mr-2 text-indigo-600" />
                Reset Password
              </h3>
              <p className="text-gray-500 mb-6 pb-4 border-b border-gray-100">
                Set a new password for: <span className="font-medium text-gray-900">{resetUser?.userName}</span>
              </p>
              
              <div className="mb-6">
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="ml-2 p-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
                    title="Generate random password"
                  >
                    <RefreshCw className="h-5 w-5 text-gray-700" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Password should be at least 8 characters and include numbers and special characters.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={isResetPasswordLoading || !newPassword}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200"
                >
                  {isResetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApprovalPage;